import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Images from "../../utils/Images";

const features = [
  {
    id: 1,
    title: "Seamless Event Discovery & Booking",
    description:
      "Attendees can easily explore upcoming conferences and trade missions, check details, and register confidently through one trusted platform.",
    image: Images.event_discovery,
  },
  {
    id: 2,
    title: "Embedded Travel Protection for Attendees",
    description:
      "Every event comes with built-in microinsurance, ensuring participants are protected throughout their journey.",
    image: Images.travel_protection,
  },
  {
    id: 3,
    title: "Professional Support for Event Organizers",
    description:
      "Organizers get powerful hosting tools for ticketing, attendee management, and promotions, backed by TripSecure's technical and insurance partners.",
    image: Images.professional_support,
  },
  {
    id: 4,
    title: "Transparent Tracking & Communication",
    description:
      "From registration to travel, attendee status and receive timely updates. Gain clear visibility over participants throughout the event lifecycle.",
    image: Images.transparent_tracking,
  },
];

export default function WhyAttendCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying || isHovered) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, isHovered]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % features.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + features.length) % features.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  return (
    <section className="px-4 py-16 bg-gradient-to-b from-white to-gray-50 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-3xl font-bold text-gray-900 sm:text-4xl lg:text-5xl">
            Why Attend or Host your Conference & Trade Mission with us
          </h2>
          <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600">
            Tripsecure empowers both participants and organizers by making
            international business events more accessible, secure and successful
          </p>
        </div>

        {/* Carousel Container */}
        <div
          className="relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Cards Container - All 4 cards visible */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4"
            >
              {features.map((feature, index) => {
                const isHighlighted = currentIndex === index;

                return (
                  <motion.div
                    key={feature.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    animate={{
                      scale: isHighlighted ? 1.05 : 1,
                      y: isHighlighted ? -8 : 0,
                    }}
                    transition={{ 
                      duration: 0.4, 
                      ease: "easeOut",
                      opacity: { duration: 0.5, delay: index * 0.1 }
                    }}
                    className="relative"
                  >
                    <div
                      className={`overflow-hidden relative h-[400px] rounded-2xl shadow-lg transition-all duration-500 group cursor-pointer ${
                        isHighlighted
                          ? "ring-2 ring-emerald-400 shadow-2xl"
                          : "hover:shadow-xl"
                      }`}
                    >
                      {/* Full Background Image */}
                      <div className="absolute inset-0">
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className="object-cover w-full h-full transition-all duration-700 group-hover:scale-110"
                        />
                      </div>

                      {/* Gradient Overlay - Darker at bottom */}
                      <div className="absolute inset-0 bg-gradient-to-t transition-all duration-300 from-black/80 via-black/40 to-black/20 group-hover:from-black/85 group-hover:via-black/50" />
                      
                      {/* Highlight indicator */}
                      {isHighlighted && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="absolute top-3 right-3 z-10"
                        >
                          <span className="inline-flex px-3 py-1 text-xs font-semibold text-white bg-emerald-600 rounded-full shadow-lg backdrop-blur-sm">
                            Featured
                          </span>
                        </motion.div>
                      )}

                      {/* Content - Positioned at bottom */}
                      <div className="absolute right-0 bottom-0 left-0 z-10 p-6">
                        <h3
                          className={`mb-2 text-sm font-bold leading-tight text-white transition-colors ${
                            isHighlighted
                              ? "text-emerald-300"
                              : "group-hover:text-emerald-300"
                          }`}
                        >
                          {feature.title}
                        </h3>
                        <p className="text-xs leading-relaxed text-white/90">
                          {feature.description}
                        </p>
                      </div>

                      {/* Highlight glow effect */}
                      {isHighlighted && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="absolute inset-0 pointer-events-none"
                        >
                          <div className="absolute inset-0 bg-gradient-to-br via-transparent to-transparent from-emerald-400/10" />
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>

          {/* Navigation Arrows - Desktop Only */}
          <div className="hidden lg:block">
            <button
              onClick={prevSlide}
              className="flex absolute left-0 top-1/2 z-10 justify-center items-center w-12 h-12 text-white rounded-full shadow-lg backdrop-blur-sm transition-all -translate-x-6 -translate-y-1/2 bg-emerald-600/90 hover:bg-emerald-600 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              aria-label="Previous slide"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextSlide}
              className="flex absolute right-0 top-1/2 z-10 justify-center items-center w-12 h-12 text-white rounded-full shadow-lg backdrop-blur-sm transition-all translate-x-6 -translate-y-1/2 bg-emerald-600/90 hover:bg-emerald-600 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              aria-label="Next slide"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        {/* Dot Indicators */}
        <div className="flex gap-2 justify-center mt-8">
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                currentIndex === index
                  ? "w-8 bg-emerald-600"
                  : "w-2 bg-gray-300 hover:bg-gray-400"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Progress Bar */}
        {isAutoPlaying && !isHovered && (
          <div className="mx-auto mt-4 max-w-md">
            <div className="overflow-hidden h-1 bg-gray-200 rounded-full">
              <motion.div
                className="h-full bg-gradient-to-r from-emerald-500 to-green-600"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "linear",
                }}
                key={currentIndex}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
